import { cn } from '@utils/cn';

import { Icon } from '../Icon';

export type StepperStep = {
  label: string;
  isCompleted: boolean;
  onClick?: () => void;
};

export type StepperProps = {
  steps: StepperStep[];
  className?: string;
};

const connectorSpacerClassName = 'min-w-6 flex-[2_1_0] basis-0';

export const Stepper = ({ className, steps }: StepperProps) => {
  const activeIndex = steps.findIndex((step) => !step.isCompleted);

  return (
    <div className={cn('w-full', className)}>
      <ol className="flex w-full items-start">
        {steps.map((step, index) => {
          const isActive = index === activeIndex;
          const isReached = step.isCompleted || isActive;
          const showConnector = index < steps.length - 1;
          const isConnectorSolid = step.isCompleted;
          const isPreviousConnectorSolid =
            index > 0 && steps[index - 1].isCompleted;

          const stepContent = (
            <>
              <div className="flex w-full items-center">
                {index > 0 ? (
                  <div
                    aria-hidden="true"
                    className={cn(
                      'flex items-center',
                      connectorSpacerClassName,
                    )}
                  >
                    <div
                      className={cn(
                        'h-0 w-full border-t',
                        isPreviousConnectorSolid
                          ? 'border-solid border-neutral-6'
                          : 'border-dashed border-divider/20',
                      )}
                    />
                  </div>
                ) : (
                  <div
                    aria-hidden="true"
                    className={connectorSpacerClassName}
                  />
                )}

                <div
                  className={cn(
                    'relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full border border-solid bg-white',
                    isReached ? 'border-neutral-6' : 'border-divider/20',
                  )}
                >
                  {step.isCompleted ? (
                    <Icon
                      className="text-neutral-6"
                      name="ic_check"
                      size="xs"
                    />
                  ) : (
                    <span
                      className={cn(
                        'text-xs leading-4',
                        isReached ? 'text-neutral-6' : 'text-neutral-4',
                      )}
                    >
                      {index + 1}
                    </span>
                  )}
                </div>

                {showConnector ? (
                  <div
                    aria-hidden="true"
                    className={cn(
                      'flex items-center',
                      connectorSpacerClassName,
                    )}
                  >
                    <div
                      className={cn(
                        'h-0 w-full border-t',
                        isConnectorSolid
                          ? 'border-solid border-neutral-6'
                          : 'border-dashed border-divider/20',
                      )}
                    />
                  </div>
                ) : (
                  <div
                    aria-hidden="true"
                    className={connectorSpacerClassName}
                  />
                )}
              </div>

              <p
                className={cn(
                  'w-full min-w-0 text-center text-xs leading-4',
                  isReached ? 'text-neutral-6' : 'text-neutral-4',
                )}
              >
                {step.label}
              </p>
            </>
          );

          return (
            <li
              key={step.label}
              className="flex min-w-0 flex-1 basis-0 flex-col items-center"
            >
              {step.onClick ? (
                <button
                  type="button"
                  onClick={step.onClick}
                  className="flex w-full cursor-pointer flex-col items-center gap-2"
                >
                  {stepContent}
                </button>
              ) : (
                <div className="flex w-full flex-col items-center gap-2">
                  {stepContent}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
